import type { ReactNode } from 'react'
import { useMemo } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import { useHealth, useMe, useThreads } from '../../hooks/queries'
import { ohneNetz } from '../../api/client'
import type { Thread } from '../../api/types'
import { initials, listTime, tripPeriod } from '../../lib/format'
import { usePreference } from '../../lib/preference'
import { Netzstreifen } from '../../components/Netzstreifen'
import {
  ArchiveIcon,
  CheckIcon,
  ChevronDownIcon,
  DoubleCheckIcon,
  HiddenIcon,
  MuteIcon,
  PinIcon,
  PlusIcon,
  SettingsIcon,
  SmileyIcon,
  TascheIcon,
} from '../../components/Icons'

/**
 * Die Chatliste.
 *
 * Für einen Empfänger steht hier je Reise ein Eintrag -- der Einzelchat mit
 * dem Reisenden. Für den Absender kommt unter jeder Reise je Empfänger ein
 * eingerückter Eintrag hinzu: dessen Antworten, die sonst niemand sieht.
 *
 * Und genau daran wächst die Liste: eine Reise mit zwölf Mitlesenden sind
 * dreizehn Zeilen. Deshalb lässt sich jede Reise einklappen, und die
 * abgeschlossenen Reisen stehen zusammengefasst am Ende.
 *
 * **Und darüber stehen die aktiven Reisen.** Wer unterwegs ist, hat eine
 * laufende Reise, die ihn gerade angeht — alles andere ist Archiv, das nur
 * noch nicht so heißt. Ist eine markiert, steht sie oben und die übrigen
 * laufenden fassen sich zu einem Abschnitt zusammen, zugeklappt.
 *
 * **Wessen Marke gilt, hängt davon ab, wer schaut** — und das war der Wunsch
 * aus dem Betrieb, sobald es mehr als einen Reisenden gibt:
 *
 * - **Wer nur mitliest, sieht alle aktiven Reisen.** Er folgt vielleicht drei
 *   Leuten; oben steht dann, woran jeder von ihnen gerade ist. Das ist genau
 *   die Auskunft, für die die Marke da ist.
 * - **Wer selbst schreibt, sieht nur seine eigene.** Für ihn ist *aktiv* die
 *   Reise, an der er arbeitet, und nicht die von jemand anderem. Reisen
 *   fremder Autoren stehen bei ihm unter „Weitere Reisen", auch wenn sie
 *   markiert sind. Sonst schöbe sich fremde Arbeit über die eigene.
 *
 * Entschieden wird das an der Rolle des **Kontos** (`may_create_trips`), nicht
 * an der Rolle in einer einzelnen Reise: ein Autor bleibt ein Autor, auch
 * während er in einer fremden Reise nur mitliest.
 *
 * **Ohne Marke bleibt die Liste, wie sie war.** Kein leerer Kopf, kein
 * Abschnitt, der nichts zusammenfasst: eine Einteilung, die nichts einteilt,
 * ist eine Zeile Aufwand für nichts.
 */
export function ChatList() {
  const threads = useThreads()
  const me = useMe()
  // Erst fragen, wenn etwas schiefgegangen ist -- im Normalfall braucht die
  // Liste diese Auskunft nicht.
  const health = useHealth(threads.isError)

  const [eingeklappt, setEingeklappt] = usePreference<number[]>('tb.chatlist.collapsed', [])
  const [archivOffen, setArchivOffen] = usePreference('tb.chatlist.archive-open', false)
  // **Zugeklappt ist die Vorgabe.** Der Sinn der Marke ist, dass der Rest aus
  // dem Weg ist; ginge der Abschnitt offen auf, wäre die Liste dieselbe wie
  // vorher, nur mit einer Überschrift mehr.
  const [restOffen, setRestOffen] = usePreference('tb.chatlist.rest-open', false)
  const [weggeraeumtOffen, setWeggeraeumtOffen] = usePreference(
    'tb.chatlist.hidden-open',
    false,
  )

  const gruppen = useMemo(() => gruppiere(threads.data ?? []), [threads.data])
  const laufende = gruppen.filter((gruppe) => !gruppe.archived)
  // Die beiden Stufen stehen getrennt, und das ist der ganze Punkt:
  // abgeschlossen heißt, es kommt nichts mehr dazu -- gelesen wird weiter.
  // Zurückgezogen heißt, niemand außer dir sieht die Reise überhaupt noch.
  // In einem gemeinsamen Abschnitt sah man den Unterschied nicht.
  const abgeschlossene = gruppen.filter((gruppe) => gruppe.archived && !gruppe.hidden)
  const zurueckgezogene = gruppen.filter((gruppe) => gruppe.hidden)

  // **Eine je Reisendem, und das entscheidet der Server.** Die Marke hängt am
  // `trips`; beim Setzen wird sie bei allen anderen Reisen **desselben
  // Besitzers** gelöscht. Zwei Autoren können also gleichzeitig eine haben,
  // und wer beiden folgt, sieht auch beide -- das ist keine Unschärfe,
  // sondern der Zweck.
  //
  // Wer selbst schreibt, sieht nur seine eigene: `role === 'sender'` ist die
  // Rolle in *dieser* Reise, `may_create_trips` die des Kontos. Beides
  // zusammen heißt: meine Reise, und ich bin Autor.
  const istAutor = me.data?.may_create_trips === true
  const aktive = laufende.filter(
    (gruppe) => gruppe.active && (!istAutor || gruppe.head.role === 'sender'),
  )
  const weitere = laufende.filter((gruppe) => !aktive.includes(gruppe))

  function umschalten(tripId: number) {
    setEingeklappt(
      eingeklappt.includes(tripId)
        ? eingeklappt.filter((id) => id !== tripId)
        : [...eingeklappt, tripId],
    )
  }

  return (
    <>
      <header className="bar">
        <h1 className="bar__title bar__grow">Reisen</h1>
        {/* Wer nur mitliest, sieht den Knopf gar nicht erst. Die Regel steht
            im Server; hier wird sie nur nicht angeboten. */}
        {me.data?.may_create_trips && (
          <Link className="icon-button" to="/new" title="Neue Reise" aria-label="Neue Reise">
            <PlusIcon />
          </Link>
        )}
        <Link
          className="icon-button"
          to="/settings"
          title="Einstellungen"
          aria-label="Einstellungen"
        >
          <SettingsIcon />
        </Link>
      </header>

      {/* Unter der Kopfzeile, die nicht mitscrollt -- hier gibt es kein
          Eingabefeld, über das er sonst gehörte. */}
      <Netzstreifen />

      <div className="threads">
        {threads.isPending && <p className="empty">Wird geladen …</p>}

        {/* Kein Netz ist kein Ausfall.
            Solange etwas im Zwischenspeicher liegt, bleibt es stehen -- und
            wenn nicht, steht hier ein ruhiger Satz statt einer Fehlermeldung.
            Aus dem Betrieb: im Flugmodus stand hier „Die Liste ließ sich nicht
            laden", als wäre etwas kaputt. Kaputt war nur der Empfang. */}
        {threads.isError && ohneNetz(threads.error) && threads.data === undefined && (
          <div className="empty">
            <p className="empty__title">Kein Netz</p>
            <p>
              Die Liste kommt, sobald du wieder Empfang hast. Was du in der
              Zwischenzeit schreibst, wartet im Gerät und geht dann von selbst
              raus.
            </p>
          </div>
        )}

        {threads.isError && !ohneNetz(threads.error) && (
          <div className="empty">
            <p className="empty__title">Die Liste ließ sich nicht laden</p>
            {/* Nach einer FTP-Auslieferung ist die häufigste Ursache eine
                Schemaänderung, die noch nicht eingespielt wurde. Dann steht
                hier, was genau fehlt -- statt dass man es im Protokoll des
                Servers suchen müsste. */}
            {(health.data?.schema_missing.length ?? 0) > 0 ? (
              <p>
                Der Datenbank fehlt, was diese Fassung erwartet:{' '}
                <code>{health.data?.schema_missing.join(', ')}</code>. Die
                Anweisung dazu steht in <code>docs/deployment-all-inkl.md</code>.
              </p>
            ) : (
              <p>{threads.error.message}</p>
            )}
          </div>
        )}

        {threads.data?.length === 0 &&
          (me.data?.may_create_trips ? (
            <div className="empty">
              <p className="empty__title">Noch keine Reise</p>
              <p>
                Lege eine Reise an und teile den Einladungslink mit denen, die
                mitlesen sollen.
              </p>
              <Link className="button" to="/new">
                Reise anlegen
              </Link>
            </div>
          ) : (
            <div className="empty">
              <p className="empty__title">Noch nichts zu lesen</p>
              <p>
                Sobald dich jemand zu einer Reise einlädt, erscheint sie hier.
              </p>
            </div>
          ))}

        {/* Die aktiven Reisen, ganz oben und ohne Klappe: sie sind der Grund,
            aus dem man die Anwendung aufmacht. Der Kopf darüber ist kein
            Knopf -- es gibt nichts zuzuklappen. */}
        {aktive.length > 0 && (
          <>
            <div className="threads__section threads__section--fest">
              <PinIcon size={17} />
              <span className="threads__section-label">Aktiv</span>
            </div>
            {aktive.map((gruppe) => (
              <TripGroup
                key={gruppe.tripId}
                group={gruppe}
                collapsed={eingeklappt.includes(gruppe.tripId)}
                onToggle={() => umschalten(gruppe.tripId)}
              />
            ))}
          </>
        )}

        {aktive.length === 0
          ? laufende.map((gruppe) => (
              <TripGroup
                key={gruppe.tripId}
                group={gruppe}
                collapsed={eingeklappt.includes(gruppe.tripId)}
                onToggle={() => umschalten(gruppe.tripId)}
              />
            ))
          : (
            /* Aufgeklappt gilt wieder die bisherige Logik -- dieselben
               Gruppen, dieselben Klappen je Reise. */
            <Abschnitt
              titel="Weitere Reisen"
              symbol={<TascheIcon size={17} />}
              gruppen={weitere}
              offen={restOffen}
              umschalten={() => setRestOffen(!restOffen)}
              eingeklappt={eingeklappt}
              reiseUmschalten={umschalten}
            />
          )}

        {/* Abgeschlossene Reisen bleiben lesbar, drängen sich aber nicht mehr
            zwischen die laufenden. Zugeklappt ist hier die Vorgabe: wer eine
            Reise abschließt, hat sie hinter sich. */}
        <Abschnitt
          titel="Abgeschlossen"
          symbol={<ArchiveIcon size={17} />}
          gruppen={abgeschlossene}
          offen={archivOffen}
          umschalten={() => setArchivOffen(!archivOffen)}
          eingeklappt={eingeklappt}
          reiseUmschalten={umschalten}
        />

        {/* Und darunter die zweite Stufe, getrennt: hier sieht die Reise
            niemand mehr außer dir. */}
        <Abschnitt
          titel="Nur für dich"
          symbol={<HiddenIcon size={17} />}
          gruppen={zurueckgezogene}
          offen={weggeraeumtOffen}
          umschalten={() => setWeggeraeumtOffen(!weggeraeumtOffen)}
          eingeklappt={eingeklappt}
          reiseUmschalten={umschalten}
        />
      </div>
    </>
  )
}

/**
 * Ein zusammengeklappter Abschnitt am Ende der Liste.
 *
 * Zweimal dasselbe Bauteil für die beiden Stufen, mit denen eine Reise zur
 * Ruhe kommt -- getrennt, weil sie Verschiedenes bedeuten: *Abgeschlossen*
 * heißt, es kommt nichts mehr dazu, gelesen wird weiter. *Nur für dich*
 * heißt, die Reise ist aus den Listen aller anderen verschwunden.
 */
function Abschnitt({
  titel,
  symbol,
  gruppen,
  offen,
  umschalten,
  eingeklappt,
  reiseUmschalten,
}: {
  titel: string
  symbol: ReactNode
  gruppen: TripThreads[]
  offen: boolean
  umschalten: () => void
  eingeklappt: number[]
  reiseUmschalten: (tripId: number) => void
}) {
  if (gruppen.length === 0) {
    return null
  }

  // **Der Knopf entscheidet, und sonst niemand.**
  //
  // Hier stand eine Zeile mehr: der Abschnitt ging von selbst auf, wenn einer
  // seiner Chats gerade offen war -- damit die Liste den offenen Chat nicht
  // wegklappt. Gut gemeint und falsch, denn sie *überstimmte* den Knopf.
  //
  // Aus dem Betrieb, und die drei Beobachtungen zeigen den Fehler genauer als
  // jede Beschreibung: „die weitere Reise lässt sich nicht einklappen am iPad.
  // am iPhone geht's beim Leser. wenn nicht der Chat angezeigt wird, sondern
  // die Einstellungen, lassen sich sonstige Reisen einklappen."
  //
  // Auf dem iPad stehen Liste **und** Chat nebeneinander -- also war immer ein
  // Chat offen, und der Abschnitt liess sich nie zuklappen. Auf dem Telefon
  // ist die Liste nur unter `/` zu sehen, wo kein Chat offen ist, und unter
  // `/settings` ebenfalls nicht. Derselbe Code, drei Bilder, ein Grund.
  //
  // **Ein Knopf, der tut, was er sagt, wiegt schwerer** als das Anliegen
  // dahinter: verloren geht der offene Chat ohnehin nicht -- man sitzt darin,
  // und sein Name steht in der Kopfzeile.
  const auf = offen

  const ungelesen = ungeleseneVon(gruppen)

  return (
    <>
      <button
        type="button"
        className="threads__section"
        aria-expanded={auf}
        onClick={umschalten}
      >
        {symbol}
        <span className="threads__section-label">
          {titel} ({gruppen.length})
        </span>
        {/* Auch wenn in einer abgeschlossenen Reise nichts mehr dazukommt:
            was vorher ungelesen blieb, soll nicht hinter dem Abschnitt
            verschwinden. */}
        {ungelesen > 0 && !auf && (
          <span className="badge badge--muted">
            {ungelesen}
            <span className="visually-hidden"> ungelesene Nachrichten</span>
          </span>
        )}
        <ChevronDownIcon
          size={18}
          className={auf ? 'fold-arrow fold-arrow--open' : 'fold-arrow'}
        />
      </button>

      {auf &&
        gruppen.map((gruppe) => (
          <TripGroup
            key={gruppe.tripId}
            group={gruppe}
            collapsed={eingeklappt.includes(gruppe.tripId)}
            onToggle={() => reiseUmschalten(gruppe.tripId)}
          />
        ))}
    </>
  )
}

function ungeleseneVon(gruppen: TripThreads[]): number {
  return gruppen.reduce(
    (summe, gruppe) =>
      gruppe.children.reduce((zahl, kind) => zahl + kind.unread, summe + gruppe.head.unread),
    0,
  )
}

interface TripThreads {
  tripId: number
  head: Thread
  children: Thread[]
  archived: boolean
  hidden: boolean
  active: boolean
}

/**
 * Aus der flachen Liste des Servers werden Reisen mit ihren Antwortverläufen.
 *
 * Die Reihenfolge kommt vom Server und bleibt, wie sie ist: dort entscheidet
 * die jüngste Regung einer Reise, und der Reise-Chat steht am Kopf seiner
 * Gruppe. Hier wird nur zusammengefasst, was zusammengehört.
 */
function gruppiere(threads: Thread[]): TripThreads[] {
  const gruppen: TripThreads[] = []

  for (const thread of threads) {
    const letzte = gruppen[gruppen.length - 1]
    if (thread.with_user === null) {
      gruppen.push({
        tripId: thread.trip_id,
        head: thread,
        children: [],
        archived: thread.archived,
        hidden: thread.hidden,
        active: thread.active,
      })
    } else if (letzte && letzte.tripId === thread.trip_id) {
      letzte.children.push(thread)
    }
  }

  return gruppen
}

function TripGroup({
  group,
  collapsed,
  onToggle,
}: {
  group: TripThreads
  collapsed: boolean
  onToggle: () => void
}) {
  // **Der Knopf entscheidet, und sonst niemand — auch hier.**
  //
  // Hier stand dieselbe Zeile wie eine Ebene höher am Abschnitt: die Reise
  // ging von selbst auf, solange einer ihrer Antwortverläufe offen war. Beim
  // Abschnitt ist sie damals entfernt worden, hier ist sie stehen geblieben.
  // Zweimal dieselbe Regel, einmal repariert — genau das, wovor die
  // Konventionen warnen.
  //
  // Aus dem Betrieb: „manchmal lässt sich die Benutzerliste der ersten Reise
  // nicht einklappen, nach ein paar mal Fenster wechseln geht es wieder
  // (andere Reise aufrufen etc.)."
  //
  // Das „manchmal" und das „geht wieder" waren der Fingerabdruck. Geprüft
  // wurde `/t/{id}/` **mit** Schrägstrich, also nur die Unterseiten — der
  // Reise-Chat selbst (`/t/{id}`) traf nicht zu. Wer in einem privaten
  // Verlauf saß, konnte dessen Reise nicht zuklappen; wer den Verlauf
  // verließ, konnte es wieder, und der Knopfdruck von vorhin wirkte
  // nachträglich. Deshalb „ein paar mal Fenster wechseln".
  //
  // **Ein Knopf, der tut, was er sagt, wiegt schwerer** als das Anliegen
  // dahinter: verloren geht der offene Verlauf ohnehin nicht -- man sitzt
  // darin, und sein Name steht in der Kopfzeile.
  const zu = collapsed && group.children.length > 0

  // Zugeklappt steht die Zeile für die ganze Reise, also zählt sie auch für
  // die ganze Reise. Ohne das verstecken sich neue Antworten hinter einem
  // Pfeil, und das Einklappen kostet Nachrichten statt Zeilen.
  const ungelesen = zu
    ? group.children.reduce((summe, kind) => summe + kind.unread, group.head.unread)
    : group.head.unread

  return (
    <>
      <div className="thread-row">
        <ThreadRow thread={group.head} unread={ungelesen} />

        {group.children.length > 0 && (
          <button
            type="button"
            className="thread-fold"
            aria-expanded={!zu}
            title={zu ? 'Antwortverläufe zeigen' : 'Antwortverläufe einklappen'}
            aria-label={
              zu
                ? `Antwortverläufe von ${group.head.trip_title} zeigen`
                : `Antwortverläufe von ${group.head.trip_title} einklappen`
            }
            onClick={onToggle}
          >
            {/* Die Zahl steht am Knopf, nicht am Titel: im schmalen Fenster
                nähme sie sonst dem Reisenamen den Platz, und der Name wiegt
                schwerer als die Zahl. */}
            {zu && <span className="thread-fold__count">{group.children.length}</span>}
            <ChevronDownIcon
              size={20}
              className={zu ? 'fold-arrow' : 'fold-arrow fold-arrow--open'}
            />
          </button>
        )}
      </div>

      {!zu && group.children.map((kind) => (
        <PartnerRow key={`${kind.trip_id}:${kind.with_user?.id}`} thread={kind} />
      ))}
    </>
  )
}

/**
 * Ein Empfänger unter seiner Reise -- eine Zeile, mehr nicht.
 *
 * Bewusst schmaler als der Reise-Chat darüber: eine Reise mit zwölf
 * Mitlesenden sind dreizehn Zeilen, und wenn jede so hoch ist wie die Reise
 * selbst, sieht man vor lauter Empfängern die Reisen nicht mehr. Deshalb
 * ohne Bild und ohne Vorschautext -- der ist hier ohnehin fast immer die
 * eigene letzte Rundmail.
 *
 * Statt dessen zwei Zahlen: wie viel in diesem Verlauf steht und wie viel
 * davon neu ist. Beide kommen aus derselben Bedingung wie der Verlauf selbst.
 *
 * **Ausgeführt, weil das Blatt „Antworten" im Chat dieselbe Zeile braucht.**
 * Auf dem Telefon ist die Liste hinter dem offenen Chat verschwunden; das
 * Blatt holt sie hervor. Eine zweite Fassung derselben Zeile wäre der Weg, auf
 * dem zwei Fassungen auseinanderlaufen — geübt hat dieses Projekt das oft
 * genug.
 */
export function PartnerRow({ thread }: { thread: Thread }) {
  const location = useLocation()
  const partner = thread.with_user
  const to = `/t/${thread.trip_id}/with/${partner?.id}`

  return (
    <NavLink
      to={to}
      className="partner-item"
      aria-current={location.pathname === to ? 'true' : undefined}
    >
      {/* Kursiv, weil stillgelegt kein Zustand der Reise ist, sondern des
          Kontos: der Verlauf bleibt, die Person kommt nicht mehr herein. */}
      <span className={partner?.disabled ? 'partner-item__name partner-item__name--still' : 'partner-item__name'}>
        {partner?.display_name}
      </span>

      {partner?.disabled && <span className="partner-item__note">stillgelegt</span>}
      {!partner?.disabled && partner?.left && (
        <span className="partner-item__note">nicht mehr dabei</span>
      )}
      {/* Erklärt das fehlende Häkchen: an ihn geht nichts mehr raus. */}
      {!partner?.disabled && !partner?.left && partner?.cut_off && (
        <span className="partner-item__note" title="Bekommt nichts Neues">
          pausiert
        </span>
      )}

      {/* Was von ihm kam: Beiträge und Zeichen getrennt. Eine Reaktion ist
          keine Wortmeldung -- und gerade der Unterschied ist die Auskunft.
          Die Null bleibt stehen statt zu verschwinden: „hat noch nie
          geschrieben" ist eine Aussage, eine leere Stelle wäre keine. */}
      <span className="partner-item__count" title={`${thread.replies} Beiträge von ihm`}>
        {thread.replies}
        <span className="visually-hidden">
          {thread.replies === 1 ? ' Beitrag geschrieben' : ' Beiträge geschrieben'}
        </span>
      </span>

      {thread.reactions > 0 && (
        <span className="partner-item__count" title={`${thread.reactions} Reaktionen`}>
          <SmileyIcon size={12} />
          {thread.reactions}
          <span className="visually-hidden"> Reaktionen</span>
        </span>
      )}

      {/* Zwei Haken heißt gelesen, einer heißt bereitgelegt -- und gar keiner
          heißt: hier wird nichts zugestellt. Eine leere Reise, jemand
          Pausierter, jemand, der nicht mehr dabei ist: ein Haken behauptete
          dort eine Zustellung, die es nicht gibt. */}
      {thread.read !== null && (
        <span
          className={thread.read ? 'partner-item__haken partner-item__haken--gelesen' : 'partner-item__haken'}
          title={thread.read ? 'Hat alles gelesen' : 'Liegt für ihn bereit, noch nicht gelesen'}
        >
          {thread.read ? <DoubleCheckIcon size={14} /> : <CheckIcon size={14} />}
          <span className="visually-hidden">
            {thread.read ? 'Hat alles gelesen' : 'Noch nicht gelesen'}
          </span>
        </span>
      )}

      {thread.unread > 0 ? (
        <span className={thread.muted ? 'badge badge--muted' : 'badge'}>
          {thread.unread}
          <span className="visually-hidden"> ungelesene Nachrichten</span>
        </span>
      ) : (
        <span className="partner-item__badge-platz" aria-hidden="true" />
      )}
    </NavLink>
  )
}

/**
 * Der Reise-Chat -- die Zeile, die für die ganze Reise steht.
 *
 * Für einen Mitlesenden ist das sein einziger Eintrag zu dieser Reise, für
 * den Absender der Kopf seiner Gruppe. Die Empfänger darunter stehen als
 * `PartnerRow` und sehen bewusst anders aus.
 */
function ThreadRow({ thread, unread }: { thread: Thread; unread?: number }) {
  const location = useLocation()
  const to = `/t/${thread.trip_id}`
  const name = thread.trip_title
  const zahl = unread ?? thread.unread
  const zeitraum = tripPeriod(thread.starts_on, thread.ends_on)

  return (
    <NavLink
      to={to}
      className="thread-item"
      aria-current={location.pathname === to ? 'true' : undefined}
    >
      {/* **Das Symbolbild steht anstelle der Buchstaben, nicht daneben.**
          Zwei Zeichen sind eine Notlösung für den Fall, dass es keines gibt —
          und genau dann bleiben sie stehen. */}
      {thread.cover_url ? (
        <img className="avatar avatar--bild" src={thread.cover_url} alt="" />
      ) : (
        <span className="avatar" aria-hidden="true">
          {initials(name)}
        </span>
      )}

      {/* Ohne Zusatz hinter dem Titel: der Abschnitt „Nur für dich" sagt es
          schon, und im schmalen Fenster nähme der Zusatz dem Reisenamen den
          Platz -- derselbe Grund, aus dem die Zahl der Verläufe am
          Einklappknopf steht und nicht am Titel. */}
      <span className="thread-item__name">{name}</span>
      <span className="thread-item__time">{listTime(thread.last_message_at)}</span>

      {/* Unter dem Titel steht der Reisezeitraum, nicht der letzte Beitrag.
          Eine Reise ist kein Gruppenchat: was zuletzt geschrieben wurde,
          steht einen Fingertipp weiter, wann die Reise war aber nirgends
          sonst. Ist kein Zeitraum eingetragen, tritt die Vorschau an seine
          Stelle -- eine leere Zeile wäre schlechter als beides. */}
      <span className="thread-item__preview">
        {zeitraum !== '' ? (
          zeitraum
        ) : (
          <>
            {thread.last_message_own && thread.last_message_preview ? 'Du: ' : ''}
            {thread.last_message_preview || 'Noch keine Nachricht'}
          </>
        )}
      </span>

      {zahl > 0 ? (
        <span className={thread.muted ? 'badge badge--muted' : 'badge'}>
          {zahl}
          <span className="visually-hidden">
            {thread.muted ? ' ungelesene Nachrichten, stumm' : ' ungelesene Nachrichten'}
          </span>
        </span>
      ) : (
        thread.muted && (
          <span className="thread-item__mute" title="Stummgeschaltet">
            <MuteIcon size={14} />
            <span className="visually-hidden">Stummgeschaltet</span>
          </span>
        )
      )}
    </NavLink>
  )
}
